import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { gql, useQuery } from '@apollo/client';
import { Virtuoso } from 'react-virtuoso';
import {
  Box,
  Stack,
  Backdrop,
  ClickAwayListener,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Popover,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { phonePrint } from '@pv/common/utils';

import some from 'lodash/some';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { Fields } from '@pv/common/components';
import { PVAvatar } from './Avatar';
import { Search, X } from 'react-feather';
import { PVHoverNavy } from '@pv/common/colors';

import { useVenues } from '../providers/venues';
import StatusChip from './StatusChip';
import { ValidStatuses } from '../types';
import {
  EventSearchQuery,
  EventSearchQueryVariables,
} from '@pv/common/graphql/operations';
import { EventSearchResult } from '@pv/common/graphql/types';
import { usePublicConfig } from '@pv/common/providers';

// Temporarily disable search - set to false to re-enable

const eventSearchQuery = gql`
  query EventSearch(
    $venueIds: [ID!]!
    $query: String
    $first: Int
    $after: String
  ) {
    eventSearch(
      venueIds: $venueIds
      query: $query
      first: $first
      after: $after
    ) {
      nodes {
        id
        name
        status
        startDate
        startOffset
        endDate
        endOffset
        planner {
          id
          fullName
          phone
          email
          gravatarUrl
        }
        additionalContacts {
          id
          fullName
          phone
          email
          gravatarUrl
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

const Planner = ({
  planner,
}: {
  planner: {
    gravatarUrl?: string | null;
    fullName?: string | null;
    phone?: string | null;
    email?: string | null;
  };
}) => {
  return (
    <Box
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        paddingBottom: '8px',
      }}
    >
      <PVAvatar
        imageUrl={planner.gravatarUrl || undefined}
        fullName={planner.fullName || ''}
      />
      <Stack direction="column">
        <Typography variant="subtitle1">
          {planner.fullName || 'Unknown'}
        </Typography>
        {planner.phone && (
          <Typography variant="body2" color="textSecondary">
            {phonePrint(planner.phone)}
          </Typography>
        )}
        {planner.email && (
          <Typography variant="body2" color="textSecondary">
            {planner.email}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

const SearchResultItem = ({
  event,
  onClick,
}: {
  event: EventSearchResult;
  onClick: () => void;
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <div
      className="event-search-result"
      onClick={onClick}
      style={{
        alignItems: 'flex-start',
        padding: '20px',
        cursor: 'pointer',
        backgroundColor: isHovered ? PVHoverNavy : 'inherit',
        borderBottom: '1px solid #E6E6E6',
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'left',
              padding: '0px 8px 16px 0px',
            }}
          >
            <Typography variant="subtitle1">
              {event.name || 'Untitled Event'}
            </Typography>
            {event.startDate && (
              <div>
                <Typography variant="body2">
                  {moment(event.startDate)
                    .startOf('day')
                    .add(event.startOffset || 0, 'minutes')
                    .format('MMM D, YYYY')}
                </Typography>
              </div>
            )}
          </div>
        </div>
        <div>
          <StatusChip status={event.status as ValidStatuses} />
        </div>
      </Box>
      <Box display="flex" flexDirection="column">
        {event.planner && <Planner planner={event.planner} />}
        {event.additionalContacts.map((contact, index) => (
          <Planner key={contact.id || index} planner={contact} />
        ))}
      </Box>
    </div>
  );
};

const SearchResults = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { venues, venueIds } = useVenues();
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [noMatches, setNoMatches] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const { publicConfig } = usePublicConfig();
  const SEARCH_ENABLED = publicConfig.eventSearchEnabled;

  const variables = { venueIds, query, first: 10 };
  const skip = !SEARCH_ENABLED || !venues || !query;
  const { data, loading, fetchMore } = useQuery<
    EventSearchQuery,
    EventSearchQueryVariables
  >(eventSearchQuery, {
    variables,
    skip,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only', // Always fetch fresh data when query changes
  });

  const matchingDocs = useMemo(() => {
    const matchingEvents = data?.eventSearch?.nodes || [];
    // Filter out null/undefined values from the nodes array
    return matchingEvents.filter(
      (event): event is EventSearchResult => event != null
    );
  }, [data]);

  const pageInfo = (data?.eventSearch as any)?.pageInfo;
  const hasNextPage = pageInfo?.hasNextPage ?? false;
  const endCursor = pageInfo?.endCursor;

  const loadMore = useCallback(async () => {
    if (!hasNextPage || loading || isLoadingMore || !endCursor) return;

    setIsLoadingMore(true);
    try {
      await fetchMore({
        variables: {
          after: endCursor,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.eventSearch) return prev;

          return {
            eventSearch: {
              ...fetchMoreResult.eventSearch,
              nodes: [
                ...(prev.eventSearch?.nodes || []),
                ...(fetchMoreResult.eventSearch.nodes || []),
              ],
            },
          };
        },
      });
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasNextPage, loading, isLoadingMore, endCursor, fetchMore]);

  const onClickSearchAndClose = (event: { id: string }) => {
    onClose();
    navigate(`/events/${event.id}`);
  };

  useEffect(() => {
    if (!loading && query && SEARCH_ENABLED) {
      if (some(matchingDocs)) {
        setNoMatches(false);
      } else {
        setNoMatches(true);
      }
    }
  }, [loading, matchingDocs, query]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setIsLoadingMore(false);
    }
  }, [open]);

  // Reset loading state when query changes
  useEffect(() => {
    setIsLoadingMore(false);
  }, [query]);
  const left = window.innerWidth / 2;
  const top = smallScreen ? 0 : 200;
  return (
    <Backdrop
      open={open}
      sx={{
        zIndex: theme.zIndex.modal - 1,
      }}
    >
      <Popover
        open={open}
        anchorReference="anchorPosition"
        anchorPosition={{ top, left }}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        sx={{
          zIndex: theme.zIndex.modal + 100,
        }}
      >
        <ClickAwayListener onClickAway={onClose}>
          <Paper
            elevation={6}
            sx={{
              width: '100%',
              overflowY: 'hidden',
              [theme.breakpoints.up('md')]: {
                width: 500,
              },
            }}
          >
            <Box
              sx={{
                padding: theme.spacing(2),
              }}
            >
              <Fields.PvTextField
                autoFocus
                inputProps={{
                  'aria-label': 'Search Events',
                }}
                placeholder="Search Events"
                value={query}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setQuery(e.target.value)
                }
                disableUnderline
                variant="standard"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={onClose}>
                        <X />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            {(matchingDocs.length > 0 ||
              noMatches ||
              (!SEARCH_ENABLED && query)) && <Divider />}
            <div style={{ height: 4 }}>
              {loading && matchingDocs.length === 0 && (
                <LinearProgress variant="query" />
              )}
            </div>
            {!SEARCH_ENABLED && query ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: theme.spacing(3),
                  height: 200,
                }}
              >
                <Typography variant="body1" color="error">
                  Search is disabled while we are making a major improvement.
                  Please use the calendar or reports to find what you're looking
                  for.
                </Typography>
              </Box>
            ) : noMatches ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: theme.spacing(3),
                  height: 200,
                }}
              >
                <Typography variant="body1" color="textSecondary">
                  No Matches
                </Typography>
              </Box>
            ) : matchingDocs.length > 0 ? (
              <Virtuoso
                data={matchingDocs}
                itemContent={(index, event) => (
                  <SearchResultItem
                    key={event.id}
                    event={event}
                    onClick={() => onClickSearchAndClose(event)}
                  />
                )}
                style={{ height: 460 }}
                endReached={loadMore}
                components={{
                  Footer: () =>
                    isLoadingMore ? (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          padding: theme.spacing(2),
                        }}
                      >
                        <LinearProgress sx={{ width: '100%' }} />
                      </Box>
                    ) : null,
                }}
              />
            ) : null}
          </Paper>
        </ClickAwayListener>
      </Popover>
    </Backdrop>
  );
};

export default SearchResults;
